import { Injectable } from '@angular/core';
import type { CrmPermissionModulesPayload } from './crm-auth-api.service';

const CRM_PERMISSION_STATE_KEY = 'crm.permission-state';

@Injectable({ providedIn: 'root' })
export class CrmPermissionStateService {
  private state: CrmPermissionModulesPayload | null = null;

  setPermissions(payload: unknown): void {
    const normalized = this.toModulesPayload(payload);
    this.state = normalized;

    if (typeof window === 'undefined') {
      return;
    }

    if (!normalized) {
      sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);
      return;
    }

    sessionStorage.setItem(CRM_PERMISSION_STATE_KEY, JSON.stringify(normalized));
  }

  clear(): void {
    this.state = null;

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);
    }
  }

  getAllPermissions(): CrmPermissionModulesPayload | null {
    return this.getPermissions();
  }

  getModulePageKeys(moduleKey: string): string[] {
    const modules = this.getPermissions();
    if (!modules) return [];
    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) return [];
    return Object.keys(moduleNode);
  }

  hasSupportedInModule(moduleKey: string): boolean {
    const modules = this.getPermissions();
    if (!modules) {
      return false;
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return false;
    }

    return Object.values(moduleNode).some((pageNode) => this.hasSupportedInNode(pageNode));
  }

  hasSupportedInPage(moduleKey: string, pageKey: string): boolean {
    const modules = this.getPermissions();
    if (!modules) {
      return false;
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return false;
    }

    return this.hasSupportedInNode(moduleNode[pageKey]);
  }

  getPageActions(moduleKey: string, pageKey: string): string[] {
    const modules = this.getPermissions();
    if (!modules) {
      return [];
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return [];
    }

    const pageNode = this.toRecord(moduleNode[pageKey]);
    if (!pageNode) {
      return [];
    }

    const rawPermissions = pageNode['permissions'];
    if (Array.isArray(rawPermissions)) {
      const actions = rawPermissions
        .filter((permission) => typeof permission === 'string')
        .map((permission) => permission.trim().toLowerCase())
        .filter((permission) => permission.length > 0);

      return [...new Set(actions)];
    }

    const permissionsNode = this.toRecord(rawPermissions);
    if (!permissionsNode) {
      return [];
    }

    const actions: string[] = [];
    for (const [action, actionNode] of Object.entries(permissionsNode)) {
      const actionRecord = this.toRecord(actionNode);
      if (!actionRecord) {
        continue;
      }

      const supported = this.toBoolean(actionRecord['supported']);
      const enabled = this.toBoolean(actionRecord['enabled']);

      if (supported || enabled) {
        actions.push(action);
      }
    }

    return actions;
  }

  private getPermissions(): CrmPermissionModulesPayload | null {
    if (this.state) {
      return this.state;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const raw = sessionStorage.getItem(CRM_PERMISSION_STATE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      const normalized = this.toModulesPayload(parsed);
      this.state = normalized;
      return normalized;
    } catch {
      sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);
      return null;
    }
  }

  private hasSupportedInNode(node: unknown): boolean {
    const nodeRecord = this.toRecord(node);
    if (!nodeRecord) {
      return false;
    }

    const rawPermissions = nodeRecord['permissions'];
    if (Array.isArray(rawPermissions)) {
      return rawPermissions.some((permission) => typeof permission === 'string' && permission.trim().length > 0);
    }

    const permissionsNode = this.toRecord(nodeRecord['permissions']);
    if (permissionsNode) {
      for (const actionNode of Object.values(permissionsNode)) {
        const actionRecord = this.toRecord(actionNode);
        if (!actionRecord) {
          continue;
        }

        if (this.toBoolean(actionRecord['supported'])) {
          return true;
        }
      }
    }

    for (const [key, value] of Object.entries(nodeRecord)) {
      if (key === 'permissions') {
        continue;
      }

      if (this.hasSupportedInNode(value)) {
        return true;
      }
    }

    return false;
  }

  private toModulesPayload(value: unknown): CrmPermissionModulesPayload | null {
    const root = this.toRecord(value);
    if (!root) {
      return null;
    }

    const normalized: Record<string, Record<string, unknown>> = {};

    for (const [moduleKey, moduleValue] of Object.entries(root)) {
      const moduleRecord = this.toRecord(moduleValue);
      if (!moduleRecord) {
        continue;
      }

      normalized[moduleKey] = moduleRecord;
    }

    return Object.keys(normalized).length > 0
      ? (normalized as unknown as CrmPermissionModulesPayload)
      : null;
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    return value as Record<string, unknown>;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
